import React, { useState, useEffect, useMemo } from "react";
import middleware from "../Api/Middleware";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "react-bootstrap";
import { faCaretLeft, faPrint, faXmark } from "@fortawesome/free-solid-svg-icons";
import * as Icons from "../HouseStyle/Icons";
import PageTemplate from "../Templates/PageTemplate";
import SendEmailModal from '../Modals/Orders/SendEmailModal';
import OrderEmailHistory from "./Components/OrderEmailHistory";
import InvoiceIconBlack from "./Icons/paper-black.png";
import PrinterIcon from "./Icons/printer.png";
import Backbutton from "../Products/subpages/images/BackIcon.svg";
import { BackButton } from "../HouseStyle/Components/Buttons";

// Component imports
import { generateInvoice } from '../generatePDF/GenerateInvoice';
import { generatePackingSlip } from '../generatePDF/GeneratePackingSlip';
import ToastSuccess from '../Toasts/ToastSuccess';
import ToastError from '../Toasts/ToastError';
import NotitieComponent from "./Components/NotitieComponent";
import CustomerComponent from "./Components/CustomerComponent";
import EmailComponents from "./Components/EmailComponent";
import VerwerkOrderComponent from "./Components/VerwerkOrderComponent";
import CrediteringComponent from "./Components/CrediteringComponent";
import VerzendComponent from "./Components/VerzendComponent";
import BetalingComponent from "./Components/BetalingComponent";
import CancelModalComponent from "./Components/CancelModalComponent";
import TerugbetalenModalComponent from "./Components/TerugbetalenModalComponent";
import PayOrderModalComponent from "./Components/PayOrderModalComponent";
import { ButtonWhite } from "../HouseStyle/Components/Buttons";
import ToastPromise from "../Toasts/ToastPromise";
import TextAreaComponent from "./Components/TextAreaComponent";

const ViewOrder = () => {
  const { order_number } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageIsLoading, setPageIsLoading] = useState(true);
  const [order, setOrder] = useState({});
  const [customer, setCustomer] = useState({});
  const [emails, setEmails] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [invoiceAddress, setInvoiceAddress] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [shownShipments, setShownShipments] = useState({});
  const [productsToProcess, setProductsToProcess] = useState([]);
  const [soldProducts, setSoldProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [payRequestModalVisible, setPayRequestModalVisible] = useState(false)
  const [paymentId, setPaymentId] = useState();
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [paybackModalVisible, setPaybackModalVisible] = useState(false);
  const [prices, setPrices] = useState({});
  const [paybackAmount, setPaybackAmount] = useState(0);
  const [creditationId, setCreditationId] = useState();
  const [selectedCreditation, setSelectedCreditation] = useState(null);
  const [exportShippingInstalled, setExportShippingInstalled] = useState(JSON.parse(localStorage.getItem("appstore")));

  const fetchAll = async () => {
    try {
      setPageIsLoading(true);

      const [orderResponse, countryCodesResponse] = await Promise.all([
        middleware.get(`orders/fullorder?order_number=${order_number}`),
        middleware.get(`countryCodes`),
      ]);

      const order = orderResponse.data.data;
      if (order === undefined || order === null) {
        ToastError(`Deze bestelling bestaat niet.`);
        navigate(`/bestellingen`);
        return;
      }
      
      setOrder(order);

      const customerType = order.internal_id?.charAt(0) === "B" ? 'businesses' : 'consumers';
      
      const { order_id, internal_id } = order;

      const [
        customerResponse,
        addressResponse,
        soldProductsResponse,
        PriceResponse,
        emailResponse,
        phoneNumberResponse,
        shipmentsResponse,
      ] = await Promise.all([
        middleware.get(`${customerType}?internal_id=${internal_id}`),
        middleware.get(`addresses?internal_id=${internal_id}`),
        middleware.get(`orders/soldproducts?order_id=${order_id}`),
        middleware.get(`/sp/orderPricesTally?order_id=${order_id}`),
        middleware.get(`emails?internal_id=${internal_id}`),
        middleware.get(`phonenumbers?internal_id=${internal_id}`),
        middleware.get(`shipments/order?order_id=${order_id}`),
      ]);

      const addressData = addressResponse?.data?.data || [];

      // Handle addresses
      if (addressData.length > 0) {
        setDeliveryAddress(addressData.find(address => order.delivery_address_id === address.address_id) || null);
        setInvoiceAddress(addressData.find(address => order.invoice_address_id === address.address_id) || null);
      }

      // Handle sold products
      const soldProducts = order.orders_sold_products;
      const unprocessedProducts = soldProducts.filter(product => product.amount - product.items_processed > 0)
        .map(product => ({
          ...product,
          amount: product.amount - product.items_processed
        }));
      setCustomer(customerResponse.data.data[0] || {});
      setEmails(emailResponse.data.data || []);
      setPhoneNumbers(phoneNumberResponse.data.data || []);
      setPrices(PriceResponse.data[0] || {});
      setProductsToProcess(unprocessedProducts);
      setSoldProducts(soldProducts);
      setProducts(soldProductsResponse.data.data)

      // Handle shipments
      const fetchedShipments = shipmentsResponse.data?.data?.Response ?? [];
      setShipments(fetchedShipments);
      setShownShipments(fetchedShipments.reduce((acc, _, index) => ({ ...acc, [index]: true }), {}));

      setPageIsLoading(false);
    } catch (error) {
      console.error(error);
      // Handle error (e.g., show error message to user)
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const calculateSalePrice = soldProduct => {
    switch (soldProduct.sale_unit) {
      case 0: // Percentage
        return Number(soldProduct.price_incl_tax) * Number(soldProduct.amount) / 100 * Number(soldProduct.sale_amount);
      case 1: // Euro
        return Number(soldProduct.sale_amount) * Number(soldProduct.amount);
      default:
        return 0;
    }
  };

  const showPrice = price => {
    return `€ ${Number(price).toFixed(2).replace('.', ',').replace(',00', ',-')}`;
  };

  const showSalePrice = soldProduct => {
    const salePrice = calculateSalePrice(soldProduct);
    return showPrice(Number(soldProduct.price_incl_tax) - salePrice / soldProduct.amount);
  };

  const handleBackButton = () => {
    if (searchParams.get("sc_return") != null) {
      navigate(`/verkoopkanalen/${searchParams.get("sc_return")}`);
    } else {
      navigate("/bestellingen");
    }
  };

  useEffect(()=>{
    async function handleAll(){
    if(searchParams.get('action')&&order.order_id)
    {
      switch(searchParams.get('action'))
      {
        case '1':
          await generatePackingSlip([order.order_id]);
          break;
          case '2':
          await generatePackingSlip([order.order_id]);
          exportShipping();
          break;
      }
      setSearchParams("");

    }}
    handleAll();
  },[order.order_id]);
  const canEdit = useMemo(() => productsToProcess.length > 0, [productsToProcess]);

  const orderStatusMap = {
    0: { label: "Geannuleerd", color: "Red" },
    1: { label: "Concept order", color: "DarkBlue" },
    2: { label: "Nieuwe bestelling", color: "Orange" },
    3: { label: "Deels in behandeling", color: "Orange" },
    4: { label: "In behandeling", color: "Blue" },
    5: { label: "Voltooid", color: "Green" }
  };

  const paymentStatusMap = {
    1: { label: "Concept betaling", color: "DarkBlue" },
    2: { label: "Openstaande betaling", color: "Orange" },
    3: { label: "Deels Betaald", color: "Orange" },
    4: { label: "Op factuur", color: "Orange" },
    5: { label: "Volledig betaald", color: "Green" },
    6: { label: "Geannuleerd", color: "Red" }
  };

  const deliveryStatusMap = {
    1: { label: "Concept verzending", color: "DarkBlue" },
    2: { label: "Deels verzonden", color: "Orange" },
    3: { label: "Verzonden", color: "Blue" },
    4: { label: "Afgeleverd", color: "Green" }
  };

  // Get status tags based on current status
  const getStatusTags = () => {
    const tags = [];

    // Add order status tag
    if (orderStatusMap[order.order_status]) {
      tags.push(orderStatusMap[order.order_status]);
    }

    // Add payment status tag with special condition
    if (order.payment_status === 0 && order.order_status === 4) {
      tags.push({ label: "Nog geen betaling", color: "Orange" });
    } else if (paymentStatusMap[order.payment_status]) {
      tags.push(paymentStatusMap[order.payment_status]);
    }

    // Add delivery status tag
    if (deliveryStatusMap[order.delivery_status]) {
      tags.push(deliveryStatusMap[order.delivery_status]);
    }

    return tags;
  };

  const saveOrderNote = async (newNote) => {
    try {
      await middleware.put(`orders`, {
        order_id: order.order_id,
        note: newNote,
      });
    } catch (error) {
      console.error(error);
      ToastError(`Er is iets fout gegaan tijdens het opslaan van de opmerking. Probeer het later opnieuw`, 'order_note_error');
    }
  }

  const saveOrderReference = async (newReference) => {
    try {
      await middleware.put(`orders`, {
        order_id: order.order_id,
        reference: newReference,
      });
    } catch (error) {
      console.error(error);
      ToastError(`Er is iets fout gegaan tijdens het opslaan van de extra informatie. Probeer het later opnieuw`, 'order_note_error');
    }
  }

  async function exportShipping()
  {
    try {
      const dt = await middleware.get(
        "orders/generateCsv/" + JSON.stringify([order.order_id])
      );

      const link = document.createElement("a");

      // Set link's href to point to the Blob URL
      link.href = URL.createObjectURL(new Blob([dt.data.data.response]));
      link.download = "shipping_address_export.csv";

      // Append link to the body
      document.body.appendChild(link);

      // Dispatch click event on the link
      link.click();
      
      // Remove link from body
      document.body.removeChild(link);
    } catch (e) {
      const er = e.response?.data?.error;
      if (er) ToastError(er);
      else console.error(e);
    }
  }

  return (
    <PageTemplate navbarTitle={`Order HUB`} pageIsLoading={pageIsLoading}>
      <CancelModalComponent cancelModalVisible={cancelModalVisible} setCancelModalVisible={setCancelModalVisible} orderId={order.order_number} />
      <TerugbetalenModalComponent
        paybackAmount={paybackAmount}
        paybackModalVisible={paybackModalVisible}
        setPaybackModalVisible={setPaybackModalVisible}
        creditationId={creditationId}
        setCreditationId={setCreditationId}
        creditation={selectedCreditation}
        setSelectedCreditation={setSelectedCreditation}
        fetchAll={fetchAll}
        order={order}
      />
      <PayOrderModalComponent payRequestModalVisible={payRequestModalVisible} setPayRequestModalVisible={setPayRequestModalVisible} totalAmount={order.total_price_incl_tax - order.amount_paid} order={order} fetchAll={fetchAll} />
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between">
              <div className="d-flex gap-3 align-items-center">
                <BackButton text={`# ${order_number}`} disabled={false} onClickFunction={() => searchParams.get('sc_return')?navigate(`/appstore/in-store/?selected=${searchParams.get('sc_return')}&page=${searchParams.get('page')}`): navigate('/bestellingen')} />
                {getStatusTags().map((tag, index) => (
                  <span
                    key={index}
                    className={`badge rounded-pill fs-6 ${tag.color ? `${tag.color}Text` : ''} py-3`}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
              
              <div className="d-flex gap-3">
                <ButtonWhite text="Pakbon" onClickFunction={() => generatePackingSlip([order.order_id])} icon={PrinterIcon} />
                {soldProducts.some(e => e.items_processed > 0) && <ButtonWhite text="Factuur" onClickFunction={() => generateInvoice([order.order_id], false, true, false)} icon={InvoiceIconBlack} />}
                {canEdit && soldProducts.find(e => e.amount - e.items_processed - e.items_credited > 0) && <ButtonWhite text="Bewerken" onClickFunction={() => navigate(`/bestellingen/bewerken/${order_number}`)} />}
                {exportShippingInstalled?.apps?.find(app => app.slug === "shipping-export")?.status === 3 && <ButtonWhite text={(<span>Exporteer bezorgadres</span>)} onClickFunction={() => exportShipping()} />}
                {order?.payments?.length > 0 && order?.delivery_status > 0 && soldProducts.find((e) => e.amount > e.items_credited) && <ButtonWhite text="Retourneren" onClickFunction={() => { setCancelModalVisible(true) }} />}
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-8">
            {/* VerwerkOrderComponent */}
            {soldProducts.find(e => e.amount - e.items_processed - e.items_credited > 0) &&
              <div>
                <VerwerkOrderComponent
                  productsToProcess={soldProducts}
                  order={order}
                  exportShipping={exportShipping}
                  generatePackingSlip={generatePackingSlip}
                />
              </div>
            }

            {/* BetalingComponent */}
            {soldProducts.find((e) => e.items_credited > 0) &&
              <div>
                <CrediteringComponent
                  setPaybackAmount={setPaybackAmount}
                  productsToProcess={soldProducts}
                  order={order}
                  customer={customer}
                  prices={prices}
                  showPrice={showPrice}
                  setPaybackModalVisible={setPaybackModalVisible}
                  creditationId={creditationId}
                  setCreditationId={setCreditationId}
                  setSelectedCreditation={setSelectedCreditation}
                />
              </div>}

            {/* VerzendComponent */}
            {soldProducts.find((e) => e.items_sent > 0) &&
              <div>
                <VerzendComponent
                  order={order}
                  setOrder={setOrder}
                  orderId={order.order_id}
                  productsToProcess={soldProducts}
                  shipmentMethod={order.shipment_method}
                  customer={customer}
                  deliveryAddress={deliveryAddress}
                  fetchAll={fetchAll}
                />
              </div>
            }
            {/* BetalingComponent */}
            <div>
              <BetalingComponent
                order={order}
                setPayRequestModalVisible={setPayRequestModalVisible}
                setPaymentId={setPaymentId}
                fetchAll={fetchAll}
              />
            </div>
          </div>

          <div className="col-lg-4">
            {/* CustomerComponent */}
            <div className="mb-4">
              <CustomerComponent
                mode="show"
                editable={false}
                customer={customer}
                order={order}
                emails={emails}
                setOrder={setOrder}
                phoneNumbers={phoneNumbers}
                setDeliveryAddress={setDeliveryAddress}
                deliveryAddress={deliveryAddress}
                invoiceAddress={invoiceAddress}
              />
            </div>

            <div className="mb-4">
              <TextAreaComponent title={'Opmerking op factuur'} placeholder="Vul een opmerking in..." text={order.note} setText={(value) => setOrder(prev => ({ ...prev, note: value }))} onSave={saveOrderNote} />
            </div>

            <div className="mb-4">
              <TextAreaComponent title={'Extra informatie'} placeholder="Vul een referentie in..." text={order.reference} setText={(value) => setOrder(prev => ({ ...prev, reference: value }))} onSave={saveOrderReference} />
            </div>

            {/* EmailComponents */}
            <div className="mb-4">
              <EmailComponents
                OrderEmailHistory={OrderEmailHistory}
                SendEmailModal={SendEmailModal}
                order={order}
                emails={emails}
                productsToProcess={productsToProcess}
              />
            </div>

            {/* NotitieComponent */}
            <NotitieComponent
              order={order}
              setOrder={setOrder}
            />
          </div>
        </div>
      </div>
    </PageTemplate >
  );
};

export default ViewOrder;